import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import type { SaveResult } from '@/components/Chat/useSyncedForm'
import { invalidateFillApplication } from '@/models/fillApplication'

export interface SavablePart {
  isDirty: boolean
  save: (orgId: string, options: { onlyChanged: boolean }) => Promise<SaveResult>
  discard: () => void
}

export interface FillCardForm {
  isDirty: boolean
  saving: boolean
  /** Saves every edited part's changed fields; true when all of them saved. */
  save: () => Promise<boolean>
  discard: () => void
  /** Whether closing would lose edits: isDirty, unless they were saved as they were made. */
  unsaved?: boolean
}

/** One card's Save and Discard over its parts, each saved on its own endpoint. */
export function useFillCardSave(orgId: string, parts: SavablePart[]): FillCardForm {
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)

  async function save(): Promise<boolean> {
    setSaving(true)
    let allSaved = true
    try {
      for (const part of parts) {
        if (!part.isDirty) continue
        // A failed part keeps its edits and its error; the others still save.
        const result = await part.save(orgId, { onlyChanged: true })
        if (!result.ok) allSaved = false
      }
    } finally {
      // Saving until the page shows what was saved, so nothing is edited or closed under the refetch.
      await invalidateFillApplication(queryClient, orgId)
      setSaving(false)
    }
    return allSaved
  }

  function discard() {
    parts.forEach((part) => part.discard())
  }

  return { isDirty: parts.some((part) => part.isDirty), saving, save, discard }
}
